export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// The summary only reports parental leave; every other leave type stays in the
// detailed records.
const PARENTAL_LEAVE_TYPES = ["maternity", "paternity"];

export function getDatesInRange(startDate, endDate) {
  const date = new Date(startDate.getTime());
  const dates = [];

  while (date <= endDate) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  return dates;
}

export function buildLeaveRanges(leaveApplications) {
  return (leaveApplications || []).map((leave) => {
    const leaveType = leave.LeaveType?.leave_name || "ON LEAVE";
    // Cancelled (2) and draft (0) applications never occupy a day.
    if (leave.leapp_status !== 2 && leave.leapp_status !== 0) {
      return {
        leaveType,
        leaveRange: getDatesInRange(
          new Date(leave.leapp_start_date),
          new Date(leave.leapp_end_date)
        ),
      };
    }
    return { leaveType, leaveRange: [] };
  });
}

export function parentalLeaveCategory(leaveType) {
  const name = String(leaveType || "").toLowerCase();
  return PARENTAL_LEAVE_TYPES.find((type) => name.includes(type)) || null;
}

// ts_duration is stored as HH.MM, so 8.45 is 8 hrs 45 mins rather than 8.45
// hours. Matches how the Total Duration row is built.
export function durationToMinutes(duration) {
  const [hoursPart, minutesPart] = String(duration ?? 0).split(".");
  const hours = parseInt(hoursPart, 10) || 0;
  const minutes = parseInt(minutesPart, 10) || 0;
  return hours * 60 + minutes;
}

/**
 * Resolves each timesheet day into one of: worked, public holiday, weekend,
 * a named leave type, or absent. Shared by the timesheet detail page and the
 * Country Program report so both report identical figures.
 */
export function decorateTimesheet(rows, employeeLeaves, publicHolidays) {
  return (rows || []).map((row) => {
    const timesheet = { ...row };
    const day = parseInt(timesheet.ts_day, 10);
    const month = parseInt(timesheet.ts_month, 10);
    const year = parseInt(timesheet.ts_year, 10);

    if (timesheet.ts_is_present === 4) {
      let leaveType = "ON LEAVE";
      const paddedMonth = `${month}`.padStart(2, "0");
      const paddedDay = `${day}`.padStart(2, "0");
      const currDate = new Date(`${year}-${paddedMonth}-${paddedDay}`);
      (employeeLeaves || []).forEach((leave) => {
        const found = leave.leaveRange.find((leaveDate) => {
          return leaveDate.getTime() === currDate.getTime();
        });
        if (found) {
          leaveType = `${leave.leaveType.toUpperCase()}`;
        }
      });
      timesheet.leaveType = leaveType;
    }

    if (timesheet.ts_duration === 0) {
      const weekday = new Date(year, month - 1, day).getDay();
      if (weekday === 6 || weekday === 0) timesheet.ts_is_present = 3;
    }

    const publicHoliday = (publicHolidays || []).find((holiday) => {
      return (
        holiday.ph_day === timesheet.ts_day &&
        holiday.ph_month === timesheet.ts_month &&
        holiday.ph_year === timesheet.ts_year
      );
    });

    if (publicHoliday) {
      timesheet.ts_is_present = 2;
      timesheet.holiday = publicHoliday.ph_name;
    }

    return timesheet;
  });
}

/**
 * Day counts for a decorated timesheet. Parental leave is split out because
 * the summary reports maternity and paternity only.
 */
export function summariseTimesheet(rows) {
  const summary = {
    daysWorked: 0,
    publicHolidayDays: 0,
    weekendDays: 0,
    maternityLeaveDays: 0,
    paternityLeaveDays: 0,
    otherLeaveDays: 0,
    absentDays: 0,
    totalDays: (rows || []).length,
    workedMinutes: 0,
  };

  (rows || []).forEach((row) => {
    switch (row.ts_is_present) {
      case 1:
        summary.daysWorked += 1;
        summary.workedMinutes += durationToMinutes(row.ts_duration);
        break;
      case 2:
        summary.publicHolidayDays += 1;
        break;
      case 3:
        summary.weekendDays += 1;
        break;
      case 4: {
        const category = parentalLeaveCategory(row.leaveType);
        if (category === "maternity") summary.maternityLeaveDays += 1;
        else if (category === "paternity") summary.paternityLeaveDays += 1;
        else summary.otherLeaveDays += 1;
        break;
      }
      default:
        summary.absentDays += 1;
    }
  });

  summary.parentalLeaveDays =
    summary.maternityLeaveDays + summary.paternityLeaveDays;
  summary.totalHours = Math.floor(summary.workedMinutes / 60);
  summary.totalMinutes = summary.workedMinutes % 60;
  summary.decimalHours = Math.round((summary.workedMinutes / 60) * 100) / 100;

  return summary;
}
